import { BackrsChip } from '@backrs_ui/common-ui';

import { Box, Typography, List, ListItem, Grid } from '@mui/material';

// Reusable styles
const styles = {
  container: (compactMode: boolean) => ({
    marginBottom: compactMode ? '10px' : '20px',
  }),
  text: (fontSize: number | string, fontWeight = 500) => ({
    fontSize,
    fontWeight,
  }),
  weekContainer: (compactMode: boolean) => ({
    padding: compactMode ? '10px 15px' : '10px 15px',
    marginBottom: compactMode ? '10px' : '15px',
    borderRadius: '16px',
    backgroundColor: '#F4F7FD',
    marginTop: '10px',
  }),
  priorityContainer: (compactMode: boolean) => ({
    padding: compactMode ? '10px 10px' : '10px 15px',
    marginBottom: compactMode ? '10px' : '15px',
    borderRadius: '16px',
    backgroundColor: '#F4F7FD',
    width: compactMode ? '50%' : '100%',
    marginTop: '10px',
    boxSizing: 'border-box',
  }),
  listItem: (compactMode: boolean) => ({
    padding: '0',
    fontSize: compactMode ? '8.5pt' : '10pt',
    marginLeft: compactMode ? '0px' : '0px',
  }),
  dayContainer: (compactMode: boolean) => ({
    backgroundColor: '#E8EEF9',
    padding: compactMode ? '5px' : '10px',
    borderRadius: '8px',
  }),
  avoidPageBreakInside: {
    pageBreakInside: 'avoid', // Prevent breaks inside this element
    breakInside: 'avoid', // For modern browsers
  },

  // Force page breaks after each week section
  pageBreakAfter: {
    pageBreakAfter: 'always',
    breakAfter: 'always', // For modern browsers
  },
};

type WeekData = Record<string, string[]>;

type renderWeekProps = Readonly<{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  weekData: WeekData | any;
  weekTitle: string;
  compactMode: boolean;
}>;

const renderWeek = ({ weekData, weekTitle, compactMode }: renderWeekProps) => (
  <Box key={weekTitle} sx={{ ...styles.avoidPageBreakInside, ...styles.pageBreakAfter }}>
    <BackrsChip
      label={`${weekTitle.charAt(0).toUpperCase()}${weekTitle.slice(1).replace(/(\d+)/, ' $1')}`}
      variant={{
        type: 'TINY_TEXT',
      }}
    />
    <Box sx={styles.weekContainer(compactMode)}>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Typography sx={styles.text(compactMode ? '8pt' : '10pt', 600)}>Goals</Typography>
          <List
            sx={{
              ...styles.listItem(compactMode),
              display: 'grid',
              gridTemplateColumns: compactMode ? '1fr 1fr' : '1fr',
            }}
          >
            {weekData.goals.map((goal: string, index: number) => (
              <ListItem key={index} sx={styles.listItem(compactMode)} disablePadding>
                <Typography
                  sx={styles.text(compactMode ? '8pt' : '10pt', 500)}
                >{`• ${goal}`}</Typography>
              </ListItem>
            ))}
          </List>
        </Grid>

        {Object.entries(weekData).map(([day, plans]) =>
          day === 'goals' ? null : (
            <Grid item xs={12} sm={4} md={6} key={day}>
              <Box sx={styles.dayContainer(compactMode)}>
                <Typography sx={styles.text(compactMode ? '7pt' : '10pt', 600)}>
                  {`${day.charAt(0).toUpperCase()}${day.slice(1)}`}
                </Typography>
                <List sx={styles.listItem(compactMode)}>
                  {(plans as string[])?.map((plan, index) => (
                    <ListItem key={index} sx={styles.listItem(compactMode)} disablePadding>
                      <Typography
                        sx={styles.text(compactMode ? '7pt' : '9pt', 500)}
                      >{`• ${plan}`}</Typography>
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Grid>
          ),
        )}
      </Grid>
    </Box>
  </Box>
);

// Priorities Section
const renderPriorities = (priorities: string[], compactMode: boolean) => (
  <Box sx={{ ...styles.container(compactMode), ...styles.avoidPageBreakInside }}>
    <BackrsChip
      label="Suggested Priorities"
      variant={{
        type: 'TINY_TEXT',
      }}
    />
    <Box sx={styles.priorityContainer(compactMode)}>
      <List
        sx={{
          ...styles.listItem(compactMode),
          display: 'grid',
          gridTemplateColumns: compactMode ? '1fr 1fr' : '1fr',
        }}
      >
        {priorities.map((priority: string, index: number) => (
          <ListItem key={index} sx={styles.listItem(compactMode)} disablePadding>
            <Typography
              sx={styles.text(compactMode ? '7.5pt' : '10pt', 500)}
            >{`${index + 1}. ${priority}`}</Typography>
          </ListItem>
        ))}
      </List>
    </Box>
  </Box>
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ActionPlanFormatter = ({ actionPlan, compactMode = false }: any) => {
  const { plan, priorities } = actionPlan;

  if (!plan || !priorities) return null;

  return (
    <Box
      sx={{
        height: '100%',
        maxHeight: '100%',
        overflowY: 'auto',
      }}
    >
      {/* Render Suggested Priorities */}
      {priorities?.priorities && renderPriorities(priorities.priorities, compactMode)}

      {/* Render Weekly Plan */}
      <Grid container spacing={compactMode ? 2 : 2}>
        {Object.entries(plan).map(([weekKey, weekData]) => (
          <Grid item xs={12} sm={6} key={weekKey}>
            {renderWeek({ weekData, weekTitle: weekKey, compactMode })}
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
